// =============================================================================
// MeuDriver — useAdminNotifications (Batch V2)
// Hooks para broadcast de notificações admin.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// ── Tipos ────────────────────────────────────────────────────────────────────

export type NotificationTargetType = 'all' | 'students' | 'instructors' | 'user'

export interface AdminBroadcastLogRow {
  id: string
  sent_by: string
  target_type: NotificationTargetType
  target_user_id: string | null
  title: string
  body: string
  action_url: string | null
  sent_at: string
}

export interface SendNotificationParams {
  target_type: NotificationTargetType
  target_user_id?: string | null
  title: string
  body: string
  action_url?: string | null
}

// ── Hook: enviar broadcast ────────────────────────────────────────────────────

export function useSendNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: SendNotificationParams) => {
      const { error } = await supabase.rpc('admin_send_notification', {
        p_target_type: params.target_type,
        p_target_user_id: params.target_user_id ?? null,
        p_title: params.title,
        p_body: params.body,
        p_action_url: params.action_url ?? null,
      } as Parameters<typeof supabase.rpc>[1])

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_broadcast_log'] })
    },
  })
}

// ── Hook: histórico de broadcasts ────────────────────────────────────────────

export function useBroadcastLog() {
  return useQuery<AdminBroadcastLogRow[]>({
    queryKey: ['admin_broadcast_log'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_broadcast_log')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(20)

      if (error) throw new Error(error.message)
      return (data ?? []) as AdminBroadcastLogRow[]
    },
    staleTime: 60_000,
  })
}
